import { TimeSeriesData } from '@delivery-metrics/shared';
import {
  Download,
  Fullscreen,
  Help,
  MoreVert,
  Refresh,
  Share,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { useDrillDownNavigation } from '../hooks/useShareableState';
import {
  formatMetricValue,
  getMetricDefinition,
  MetricDefinition,
} from '../utils/metricDefinitions';
import { copyShareableURL } from '../utils/shareableUrl';

export type ChartType = 'line' | 'area' | 'bar' | 'pie' | 'donut';

export interface ChartDataPoint {
  [key: string]: any;
  timestamp?: Date | string;
  label?: string;
}

export interface ChartSeries {
  key: string;
  name: string;
  color?: string;
  type?: ChartType;
}

export interface InteractiveChartProps {
  title: string;
  subtitle?: string;
  data: ChartDataPoint[];
  series: ChartSeries[];
  type?: ChartType;
  height?: number;
  loading?: boolean;
  error?: string;
  showLegend?: boolean;
  showGrid?: boolean;
  showTooltip?: boolean;
  enableZoom?: boolean;
  enablePan?: boolean;
  enableDrillDown?: boolean;
  enableShare?: boolean;
  xAxisKey?: string;
  yAxisLabel?: string;
  xAxisLabel?: string;
  colors?: string[];
  metricDefinitions?: Record<string, MetricDefinition>;
  onDataPointClick?: (data: ChartDataPoint, series: ChartSeries) => void;
  onExport?: (format: 'png' | 'svg' | 'pdf') => void;
  onRefresh?: () => void;
  onDrillDown?: (
    level: string,
    data: ChartDataPoint,
    series: ChartSeries
  ) => void;
  customTooltip?: (props: any) => React.ReactElement | null;
  referenceLines?: Array<{
    value: number;
    label?: string;
    color?: string;
    strokeDasharray?: string;
  }>;
}

const DEFAULT_COLORS = [
  '#1976d2',
  '#dc004e',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#0097a7',
  '#5d4037',
  '#616161',
];

export const InteractiveChart: React.FC<InteractiveChartProps> = ({
  title,
  subtitle,
  data,
  series,
  type = 'line',
  height = 300,
  loading = false,
  error,
  showLegend = true,
  showGrid = true,
  showTooltip = true,
  enableZoom = false,
  enablePan = false,
  enableDrillDown = false,
  enableShare = false,
  xAxisKey = 'timestamp',
  yAxisLabel,
  xAxisLabel,
  colors = DEFAULT_COLORS,
  metricDefinitions = {},
  onDataPointClick,
  onExport,
  onRefresh,
  onDrillDown,
  customTooltip,
  referenceLines = [],
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tooltipAnchor, setTooltipAnchor] = useState<null | HTMLElement>(null);
  const [tooltipContent, setTooltipContent] = useState<MetricDefinition | null>(
    null
  );
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const { drillDown, drillDownPath } = useDrillDownNavigation();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleShare = useCallback(async () => {
    try {
      const success = await copyShareableURL({} as any, {
        drillDownPath,
      });
      if (success) {
        setShareSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Failed to share chart:', error);
    }
    handleMenuClose();
  }, [drillDownPath]);

  const handleMetricHover = useCallback(
    (event: React.MouseEvent, metricKey: string) => {
      const definition =
        metricDefinitions[metricKey] || getMetricDefinition(metricKey);
      if (definition) {
        setTooltipAnchor(event.currentTarget as HTMLElement);
        setTooltipContent(definition);
      }
    },
    [metricDefinitions]
  );

  const handleMetricHoverEnd = useCallback(() => {
    setTooltipAnchor(null);
    setTooltipContent(null);
  }, []);

  const handleExport = (format: 'png' | 'svg' | 'pdf') => {
    onExport?.(format);
    handleMenuClose();
  };

  const formatXAxisTick = (value: any) => {
    if (
      value instanceof Date ||
      (typeof value === 'string' && !isNaN(Date.parse(value)))
    ) {
      const date = new Date(value);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
    return value;
  };

  const formatYAxisTick = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    if (customTooltip) {
      return customTooltip({ active, payload, label });
    }

    // Format the label - handle Date objects and strings
    let formattedLabel = label;
    if (label instanceof Date) {
      formattedLabel = label.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } else if (typeof label === 'string' && !isNaN(Date.parse(label))) {
      formattedLabel = new Date(label).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    return (
      <Paper
        elevation={3}
        sx={{
          p: 2,
          maxWidth: 300,
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
          {formattedLabel}
        </Typography>
        {payload.map((entry: any, index: number) => {
          const metricDef =
            metricDefinitions[entry.dataKey] ||
            getMetricDefinition(entry.dataKey);
          const formattedValue = metricDef
            ? formatMetricValue(entry.value, metricDef)
            : typeof entry.value === 'number'
              ? entry.value.toLocaleString()
              : entry.value;

          return (
            <Box key={index} sx={{ mb: 1 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: entry.color,
                    borderRadius: '50%',
                  }}
                />
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {entry.name}: {formattedValue}
                </Typography>
              </Box>
              {metricDef && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ ml: 2, display: 'block' }}
                >
                  {metricDef.description}
                </Typography>
              )}
            </Box>
          );
        })}
      </Paper>
    );
  };

  const handleDataPointClick = (data: any, series: ChartSeries) => {
    if (enableDrillDown && onDrillDown) {
      onDrillDown(`${series.key}-detail`, data, series);
      drillDown(`${series.key}-detail`, data);
    } else if (onDataPointClick) {
      onDataPointClick(data, series);
    }
  };

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
    };

    const xAxisProps = {
      dataKey: xAxisKey,
      tickFormatter: formatXAxisTick,
      label: xAxisLabel
        ? { value: xAxisLabel, position: 'insideBottom', offset: -10 }
        : undefined,
    };

    const yAxisProps = {
      tickFormatter: formatYAxisTick,
      label: yAxisLabel
        ? { value: yAxisLabel, angle: -90, position: 'insideLeft' }
        : undefined,
    };

    switch (type) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray='3 3' />}
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            {showTooltip && <RechartsTooltip content={<CustomTooltip />} />}
            {showLegend && <Legend />}
            {referenceLines.map((line, index) => (
              <ReferenceLine
                key={index}
                y={line.value}
                stroke={line.color || '#666'}
                strokeDasharray={line.strokeDasharray || '5 5'}
                label={line.label}
              />
            ))}
            {series.map((s, index) => (
              <Area
                key={s.key}
                dataKey={s.key}
                name={s.name}
                stroke={s.color || colors[index % colors.length]}
                fill={s.color || colors[index % colors.length]}
                fillOpacity={0.3}
                onClick={data => handleDataPointClick(data, s)}
                style={{ cursor: enableDrillDown ? 'pointer' : 'default' }}
              />
            ))}
          </AreaChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray='3 3' />}
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            {showTooltip && <RechartsTooltip content={<CustomTooltip />} />}
            {showLegend && <Legend />}
            {referenceLines.map((line, index) => (
              <ReferenceLine
                key={index}
                y={line.value}
                stroke={line.color || '#666'}
                strokeDasharray={line.strokeDasharray || '5 5'}
                label={line.label}
              />
            ))}
            {series.map((s, index) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                fill={s.color || colors[index % colors.length]}
                onClick={data => handleDataPointClick(data, s)}
                style={{ cursor: enableDrillDown ? 'pointer' : 'default' }}
              />
            ))}
          </BarChart>
        );

      case 'pie':
      case 'donut': {
        const pieData = data.map((item, index) => ({
          ...item,
          fill: colors[index % colors.length],
        }));

        return (
          <PieChart {...commonProps}>
            {showTooltip && <RechartsTooltip content={<CustomTooltip />} />}
            {showLegend && <Legend />}
            <Pie
              data={pieData}
              cx='50%'
              cy='50%'
              innerRadius={type === 'donut' ? 60 : 0}
              outerRadius={80}
              paddingAngle={2}
              dataKey={series[0]?.key || 'value'}
              onClick={data => handleDataPointClick(data, series[0])}
              style={{ cursor: enableDrillDown ? 'pointer' : 'default' }}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        );
      }

      default: // line
        return (
          <LineChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray='3 3' />}
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            {showTooltip && <RechartsTooltip content={<CustomTooltip />} />}
            {showLegend && <Legend />}
            {referenceLines.map((line, index) => (
              <ReferenceLine
                key={index}
                y={line.value}
                stroke={line.color || '#666'}
                strokeDasharray={line.strokeDasharray || '5 5'}
                label={line.label}
              />
            ))}
            {series.map((s, index) => (
              <Line
                key={s.key}
                type='monotone'
                dataKey={s.key}
                name={s.name}
                stroke={s.color || colors[index % colors.length]}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                onClick={data => handleDataPointClick(data, s)}
                style={{ cursor: enableDrillDown ? 'pointer' : 'default' }}
              />
            ))}
          </LineChart>
        );
    }
  };

  if (error) {
    return (
      <Card sx={{ height: height + 100 }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            {title}
          </Typography>
          <Alert severity='error' sx={{ mt: 2 }}>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: height + 100 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Box>
            <Typography variant='h6' component='h3'>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant='body2' color='text.secondary'>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {enableShare && (
              <Tooltip title='Share Chart'>
                <IconButton size='small' onClick={handleShare}>
                  <Share />
                </IconButton>
              </Tooltip>
            )}
            {onRefresh && (
              <Tooltip title='Refresh'>
                <IconButton size='small' onClick={onRefresh}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size='small' onClick={handleMenuOpen}>
              <MoreVert />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            height,
            position: 'relative',
            overflow: 'hidden',
          }}
          ref={chartRef}
        >
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <ResponsiveContainer width='100%' height='100%'>
              {renderChart()}
            </ResponsiveContainer>
          )}
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {onExport && [
            <MenuItem key='png' onClick={() => handleExport('png')}>
              <Download sx={{ mr: 1 }} />
              Export as PNG
            </MenuItem>,
            <MenuItem key='svg' onClick={() => handleExport('svg')}>
              <Download sx={{ mr: 1 }} />
              Export as SVG
            </MenuItem>,
            <MenuItem key='pdf' onClick={() => handleExport('pdf')}>
              <Download sx={{ mr: 1 }} />
              Export as PDF
            </MenuItem>,
          ]}
          {enableShare && (
            <MenuItem onClick={handleShare}>
              <Share sx={{ mr: 1 }} />
              Share Chart
            </MenuItem>
          )}
          <MenuItem onClick={handleMenuClose}>
            <Fullscreen sx={{ mr: 1 }} />
            Full Screen
          </MenuItem>
          <MenuItem
            onClick={e => {
              handleMetricHover(e, series[0]?.key || '');
              handleMenuClose();
            }}
          >
            <Help sx={{ mr: 1 }} />
            Metric Info
          </MenuItem>
        </Menu>

        {/* Metric Definition Tooltip */}
        <Popper
          open={Boolean(tooltipAnchor)}
          anchorEl={tooltipAnchor}
          placement='top'
          sx={{ zIndex: 1300 }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 2,
              maxWidth: 400,
              backgroundColor: 'background.paper',
              border: 1,
              borderColor: 'divider',
            }}
            onMouseLeave={handleMetricHoverEnd}
          >
            {tooltipContent && (
              <>
                <Typography variant='h6' gutterBottom>
                  {tooltipContent.name}
                </Typography>
                <Typography variant='body2' color='text.secondary' paragraph>
                  {tooltipContent.description}
                </Typography>
                {tooltipContent.formula && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant='caption' color='text.secondary'>
                      Formula:
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ fontFamily: 'monospace', ml: 1 }}
                    >
                      {tooltipContent.formula}
                    </Typography>
                  </Box>
                )}
                {tooltipContent.interpretation && (
                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Interpretation:
                    </Typography>
                    {tooltipContent.interpretation.good && (
                      <Typography
                        variant='body2'
                        color='success.main'
                        sx={{ ml: 1 }}
                      >
                        Good: {tooltipContent.interpretation.good}
                      </Typography>
                    )}
                    {tooltipContent.interpretation.warning && (
                      <Typography
                        variant='body2'
                        color='warning.main'
                        sx={{ ml: 1 }}
                      >
                        Warning: {tooltipContent.interpretation.warning}
                      </Typography>
                    )}
                    {tooltipContent.interpretation.critical && (
                      <Typography
                        variant='body2'
                        color='error.main'
                        sx={{ ml: 1 }}
                      >
                        Critical: {tooltipContent.interpretation.critical}
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Popper>

        {/* Share Success Snackbar */}
        <Snackbar
          open={shareSnackbarOpen}
          autoHideDuration={3000}
          onClose={() => setShareSnackbarOpen(false)}
          message='Chart URL copied to clipboard!'
        />
      </CardContent>
    </Card>
  );
};

// Helper function to convert TimeSeriesData to chart format
export const convertTimeSeriesData = (
  data: TimeSeriesData[],
  seriesKey: string = 'value'
): ChartDataPoint[] => {
  return data.map(point => ({
    timestamp: point.timestamp,
    [seriesKey]: point.value,
    label: point.label,
  }));
};
